// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import * as api from '../api/client'

const TABS = ['Home', 'Patients', 'Intake', 'Plan of Care', 'Reports']

/**
 * Public marketing landing page (bare product apex). The form is the conversion goal; the animated
 * product preview cycles through the app's tabs with ABSTRACT skeleton screens — no patients, no PHI.
 * All motion respects prefers-reduced-motion.
 */
export default function MarketingLanding() {
  const [form, setForm] = useState({ name: '', email: '', organization: '', message: '' })
  const [honey, setHoney] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle')
  const [err, setErr] = useState<string | null>(null)
  const [tab, setTab] = useState(0)

  useEffect(() => {
    const els = Array.from(document.querySelectorAll('[data-reveal]'))
    if (!('IntersectionObserver' in window)) { els.forEach(e => e.classList.add('in')); return }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target) } })
    }, { threshold: 0.18 })
    els.forEach(e => io.observe(e))
    return () => io.disconnect()
  }, [])

  // Cycle the active tab so the preview reads as live navigation. Paused under reduced-motion.
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
    const id = setInterval(() => setTab(t => (t + 1) % TABS.length), 2600)
    return () => clearInterval(id)
  }, [])

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }))

  async function submit() {
    setErr(null)
    if (!form.name.trim()) { setErr('Please enter your name.'); return }
    if (!/.+@.+\..+/.test(form.email)) { setErr('Please enter a valid email address.'); return }
    setState('sending')
    try {
      await api.addDemoRequest({
        name: form.name, email: form.email,
        organization: form.organization || undefined,
        message: form.message || undefined,
        company_website: honey || undefined,
      })
      setState('done')
    } catch (e: any) {
      setErr(e?.payload?.message || 'Something went wrong sending your request. Please try again.')
      setState('error')
    }
  }

  return (
    <div className="ml-root">
      <style>{CSS}</style>

      {/* HERO */}
      <header className="ml-hero">
        <div className="ml-aurora" aria-hidden="true" />
        <div className="ml-hero-inner">
          <div className="ml-copy">
            <span className="ml-eyebrow ml-rise" style={{ animationDelay: '40ms' }}>OwlRx — by Owl Consultations</span>
            <h1 className="ml-h1 ml-rise" style={{ animationDelay: '120ms' }}>Specialty pharmacy, run on one clinical workflow.</h1>
            <p className="ml-lede ml-rise" style={{ animationDelay: '220ms' }}>
              OwlRx brings enrollment, screening, and plan of care into a single HIPAA-secure system —
              built around how specialty and infusion pharmacies actually work.
            </p>
            <ul className="ml-points ml-rise" style={{ animationDelay: '320ms' }}>
              <li><span className="ml-tick">✓</span> From enrollment to plan of care, in one place</li>
              <li><span className="ml-tick">✓</span> Built for specialty &amp; infusion therapy</li>
              <li><span className="ml-tick">✓</span> HIPAA-secure, audited, and integrated with your pharmacy system</li>
            </ul>
          </div>

          <div className="ml-card ml-rise" style={{ animationDelay: '300ms' }}>
            {state === 'done' ? (
              <div style={{ padding: '8px 4px' }}>
                <div className="ml-card-title">Request received</div>
                <p className="ml-card-sub" style={{ marginTop: 10 }}>
                  Thanks, {form.name.trim().split(' ')[0] || 'there'} — your request is in. We'll be in touch
                  at <strong>{form.email}</strong>.
                </p>
              </div>
            ) : (
              <>
                <div className="ml-card-title">Request a demo</div>
                <p className="ml-card-sub">See OwlRx with your own workflow in mind. We'll reach out to set up a walkthrough.</p>
                <label className="ml-lbl" htmlFor="dm-name">Name</label>
                <input id="dm-name" className="ml-input" value={form.name} onChange={set('name')} autoComplete="name" />
                <label className="ml-lbl" htmlFor="dm-email">Work email</label>
                <input id="dm-email" className="ml-input" value={form.email} onChange={set('email')} type="email" autoComplete="email" inputMode="email" />
                <label className="ml-lbl" htmlFor="dm-org">Pharmacy / organization</label>
                <input id="dm-org" className="ml-input" value={form.organization} onChange={set('organization')} autoComplete="organization" />
                <label className="ml-lbl" htmlFor="dm-msg">What would you like to see? <span style={{ color: 'var(--color-text-mute)', fontWeight: 400 }}>(optional)</span></label>
                <textarea id="dm-msg" className="ml-input" style={{ minHeight: 70, resize: 'vertical' }} value={form.message} onChange={set('message')} />
                <input tabIndex={-1} autoComplete="off" aria-hidden="true" value={honey} onChange={e => setHoney(e.target.value)}
                  style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />
                {err && <div role="alert" className="ml-err">{err}</div>}
                <button className="ml-btn" onClick={submit} disabled={state === 'sending'}>
                  {state === 'sending' ? 'Sending…' : 'Request a demo'}
                </button>
                <p className="ml-fine">By submitting, you agree to be contacted about OwlRx. No spam.</p>
              </>
            )}
          </div>
        </div>
      </header>

      {/* PRODUCT PREVIEW — abstract skeleton screens, no patients / no PHI */}
      <section className="ml-preview-band">
        <div className="ml-preview-head" data-reveal>
          <span className="ml-eyebrow" style={{ color: 'var(--color-primary)' }}>See it in action</span>
          <h2 className="ml-h2">One system, every step of the workflow.</h2>
        </div>

        <div className="ml-frame" data-reveal>
          <div className="ml-frame-bar">
            <span className="ml-dot" /><span className="ml-dot" /><span className="ml-dot" />
            <span className="ml-frame-url">OwlRx · Your Future System</span>
          </div>
          <div className="ml-frame-body">
            <nav className="ml-side">
              {TABS.map((n, i) => (
                <button key={n} className={'ml-nav' + (i === tab ? ' ml-nav-on' : '')} onClick={() => setTab(i)}>{n}</button>
              ))}
            </nav>
            <div className="ml-main">
              <div className="ml-screen-title">{TABS[tab]}</div>
              <div key={tab} className="ml-screen">
                <Screen tab={tab} />
              </div>
            </div>
          </div>
        </div>
        <p className="ml-preview-note" data-reveal>An animated preview — abstract screens only, no patient data. We'll walk you through the live product on a demo.</p>
      </section>

      {/* CAPABILITIES */}
      <section className="ml-band">
        {[
          { h: 'Intake & screening', b: "Structured enrollment and screening that keep a patient from advancing before they're ready — every step on the record." },
          { h: 'Plan of care', b: 'Clinical documentation and certification shaped for biologic and infusion therapy, not bolted on from retail.' },
          { h: 'Secure by design', b: 'HIPAA-aligned access, an append-only audit trail, and integration with the pharmacy system you already run.' },
        ].map((c, i) => (
          <div key={c.h} className="ml-col" data-reveal style={{ transitionDelay: `${i * 110}ms` }}>
            <h3 className="ml-col-h">{c.h}</h3>
            <p className="ml-col-b">{c.b}</p>
          </div>
        ))}
      </section>

      <footer className="ml-footer">
        <span>© {new Date().getFullYear()} Owl Consultations</span>
        <span className="ml-foot-links">
          <a href="/legal/privacy">Privacy</a>
          <a href="/legal/terms">Terms</a>
          <span style={{ color: 'var(--color-text-mute)' }}>Already a customer? Use the pharmacy link from your administrator.</span>
        </span>
      </footer>
    </div>
  )
}

/** Abstract skeleton screen per tab — purely visual shapes, never any data. */
function Screen({ tab }: { tab: number }) {
  const bar = (w: string, h = 10) => <div className="sk-bar" style={{ width: w, height: h }} />
  if (tab === 0) return ( // Home — stat tiles + chart
    <>
      <div className="sk-tiles">
        {[0, 1, 2].map(i => <div key={i} className="sk-tile">{bar('44%', 8)}{bar('62%', 18)}</div>)}
      </div>
      <div className="sk-chart">{[40, 70, 52, 86, 60, 94, 72].map((h, i) => <span key={i} style={{ height: `${h}%` }} />)}</div>
    </>
  )
  if (tab === 1) return ( // Patients — list rows (no names, no MRNs)
    <div className="sk-list">
      {[0, 1, 2, 3].map(i => (
        <div key={i} className="sk-row">
          <span className="sk-avatar" />
          <div style={{ flex: 1 }}>{bar('52%', 9)}<div style={{ height: 6 }} />{bar('34%', 7)}</div>
          <span className="sk-pill" />
        </div>
      ))}
    </div>
  )
  if (tab === 2) return ( // Intake — form skeleton
    <div className="sk-form">
      {[0, 1, 2, 3].map(i => (
        <div key={i}>{bar(['24%', '30%', '20%', '28%'][i], 7)}<div className="sk-input" /></div>
      ))}
      <div className="sk-submit" />
    </div>
  )
  if (tab === 3) return ( // Plan of Care — lifecycle stepper
    <div className="sk-stage-row">
      {['Enrollment', 'Screening', 'Plan of care', 'Fulfillment'].map(s => (
        <div key={s} className="sk-stage"><span className="sk-stage-dot" />{s}</div>
      ))}
      <div className="sk-track"><div className="sk-fill" /></div>
    </div>
  )
  return ( // Reports — bar chart + legend
    <>
      <div className="sk-chart sk-chart-tall">{[54, 78, 42, 90, 66, 50, 82, 70].map((h, i) => <span key={i} style={{ height: `${h}%` }} />)}</div>
      <div className="sk-legend">{[0, 1, 2].map(i => <span key={i}><i /> </span>)}</div>
    </>
  )
}

const CSS = `
.ml-root { min-height:100vh; background:var(--color-bg); color:var(--color-text); overflow-x:hidden; }

@keyframes mlRise { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:none; } }
.ml-rise { opacity:0; animation:mlRise .7s cubic-bezier(.2,.7,.2,1) forwards; }
[data-reveal] { opacity:0; transform:translateY(20px); transition:opacity .7s ease, transform .7s cubic-bezier(.2,.7,.2,1); }
[data-reveal].in { opacity:1; transform:none; }

.ml-hero { position:relative; background:linear-gradient(160deg,#0b3d39 0%,#0f5750 100%); color:#f3f7f6; overflow:hidden; }
.ml-aurora { position:absolute; inset:-40% -20% auto -20%; height:520px; pointer-events:none;
  background:radial-gradient(60% 60% at 30% 20%, rgba(127,216,205,.28), transparent 60%),
             radial-gradient(50% 50% at 80% 10%, rgba(15,118,110,.40), transparent 60%);
  animation:mlDrift 16s ease-in-out infinite alternate; }
@keyframes mlDrift { from { transform:translate3d(0,0,0) scale(1); } to { transform:translate3d(0,28px,0) scale(1.06); } }
.ml-hero-inner { position:relative; max-width:1080px; margin:0 auto; padding:64px 24px 72px; display:flex; gap:48px; align-items:center; flex-wrap:wrap; }
.ml-copy { flex:1 1 360px; min-width:300px; }
.ml-eyebrow { font-size:12px; font-weight:700; letter-spacing:.14em; text-transform:uppercase; color:#7fd8cd; display:block; }
.ml-h1 { font-size:clamp(32px,5vw,48px); line-height:1.08; font-weight:800; letter-spacing:-.02em; margin:14px 0 0; }
.ml-lede { font-size:17px; line-height:1.6; color:#cfe6e2; margin:18px 0 0; max-width:520px; }
.ml-points { list-style:none; padding:0; margin:24px 0 0; display:grid; gap:10px; }
.ml-points li { font-size:15px; color:#e3efed; display:flex; gap:10px; align-items:flex-start; }
.ml-tick { color:#7fd8cd; font-weight:800; }

.ml-card { flex:0 1 380px; min-width:300px; background:var(--color-surface); color:var(--color-text); border-radius:14px; padding:24px; box-shadow:0 24px 60px -24px rgba(0,0,0,.45); position:relative; }
.ml-card-title { font-size:20px; font-weight:800; letter-spacing:-.01em; }
.ml-card-sub { font-size:13.5px; color:var(--color-text-soft); line-height:1.5; margin:6px 0 16px; }
.ml-lbl { display:block; font-size:12.5px; font-weight:600; margin:12px 0 5px; color:var(--color-text-soft); }
.ml-input { width:100%; box-sizing:border-box; padding:9px 11px; font-size:14px; font-family:inherit; border:1px solid var(--color-border); border-radius:8px; background:var(--color-bg); color:var(--color-text); transition:border-color .15s ease, box-shadow .15s ease; }
.ml-input:focus { outline:none; border-color:var(--color-primary); box-shadow:0 0 0 3px var(--color-primary-soft); }
.ml-btn { width:100%; margin-top:18px; padding:11px 14px; font-size:15px; font-weight:700; font-family:inherit; color:#fff; background:var(--color-primary); border:none; border-radius:9px; cursor:pointer; transition:transform .12s ease, background .15s ease, box-shadow .15s ease; }
.ml-btn:hover:not(:disabled) { background:var(--color-primary-hover); transform:translateY(-1px); box-shadow:0 10px 24px -12px rgba(15,118,110,.7); }
.ml-btn:disabled { opacity:.7; cursor:default; }
.ml-fine { font-size:11.5px; color:var(--color-text-mute); margin:10px 0 0; text-align:center; }
.ml-err { margin-top:14px; padding:9px 11px; font-size:13px; color:#7f1d1d; background:#fef2f2; border:1px solid #fecaca; border-radius:8px; }

.ml-preview-band { max-width:1080px; margin:0 auto; padding:64px 24px 8px; }
.ml-preview-head { text-align:center; margin-bottom:28px; }
.ml-h2 { font-size:clamp(24px,3.4vw,32px); font-weight:800; letter-spacing:-.02em; margin:10px 0 0; }

.ml-frame { max-width:880px; margin:0 auto; background:var(--color-surface); border:1px solid var(--color-border); border-radius:14px; overflow:hidden; box-shadow:0 30px 70px -34px rgba(0,0,0,.4); }
.ml-frame-bar { display:flex; align-items:center; gap:7px; padding:11px 14px; background:var(--color-surface-alt); border-bottom:1px solid var(--color-border); }
.ml-dot { width:10px; height:10px; border-radius:999px; background:var(--color-border); }
.ml-frame-url { margin-left:12px; font-size:12px; color:var(--color-text-mute); }
.ml-frame-body { display:flex; min-height:300px; }
.ml-side { width:152px; flex-shrink:0; border-right:1px solid var(--color-border); padding:14px 10px; display:grid; gap:4px; align-content:start; }
.ml-nav { text-align:left; font-family:inherit; font-size:13px; color:var(--color-text-soft); padding:8px 11px; border-radius:7px; border:none; background:transparent; cursor:pointer; transition:background .25s ease, color .25s ease; }
.ml-nav-on { background:var(--color-primary-soft); color:#0f5750; font-weight:700; }
.ml-main { flex:1; padding:20px 22px; }
.ml-screen-title { font-size:15px; font-weight:800; letter-spacing:-.01em; margin-bottom:16px; }
@keyframes mlScreenIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:none; } }
.ml-screen { animation:mlScreenIn .45s ease; }

/* skeleton primitives */
.sk-bar { background:var(--color-border); border-radius:5px; }
.sk-tiles { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; margin-bottom:18px; }
.sk-tile { border:1px solid var(--color-border); border-radius:10px; padding:14px; display:grid; gap:9px; }
.sk-chart { display:flex; align-items:flex-end; gap:9px; height:118px; padding-top:6px; }
.sk-chart span { flex:1; background:linear-gradient(180deg,#14b8a6,#0f766e); border-radius:5px 5px 0 0; opacity:.85; }
.sk-chart-tall { height:150px; }
.sk-legend { display:flex; gap:16px; margin-top:12px; }
.sk-legend i { display:inline-block; width:22px; height:8px; border-radius:4px; background:var(--color-primary-soft); }
.sk-list { display:grid; gap:10px; }
.sk-row { display:flex; align-items:center; gap:12px; padding:12px 14px; border:1px solid var(--color-border); border-radius:10px; }
.sk-avatar { width:30px; height:30px; border-radius:999px; background:var(--color-surface-alt); flex-shrink:0; }
.sk-pill { width:58px; height:18px; border-radius:999px; background:var(--color-primary-soft); flex-shrink:0; }
.sk-form { display:grid; gap:14px; max-width:430px; }
.sk-input { margin-top:7px; height:34px; border:1px solid var(--color-border); border-radius:8px; background:var(--color-bg); }
.sk-submit { width:130px; height:36px; border-radius:8px; background:var(--color-primary); opacity:.85; }
.sk-stage-row { position:relative; display:flex; justify-content:space-between; padding:18px 0 8px; }
.sk-stage { display:flex; flex-direction:column; align-items:center; gap:9px; font-size:11.5px; font-weight:600; color:var(--color-text-soft); width:25%; z-index:1; }
.sk-stage-dot { width:13px; height:13px; border-radius:999px; background:var(--color-surface); border:2px solid var(--color-border); }
.sk-track { position:absolute; top:24px; left:12%; right:12%; height:3px; background:var(--color-border); border-radius:2px; z-index:0; }
.sk-fill { height:100%; width:0; border-radius:2px; background:linear-gradient(90deg,#0f766e,#14b8a6); box-shadow:0 0 10px rgba(20,184,166,.6); animation:mlFill 4s cubic-bezier(.6,0,.3,1) infinite; }
@keyframes mlFill { 0% { width:6%; } 40% { width:46%; } 75% { width:78%; } 100% { width:100%; } }

.ml-preview-note { text-align:center; font-size:13px; color:var(--color-text-mute); margin:22px 0 0; }

.ml-band { max-width:1080px; margin:0 auto; padding:48px 24px 8px; display:grid; grid-template-columns:repeat(auto-fit,minmax(220px,1fr)); gap:32px; }
.ml-col { border-top:2px solid var(--color-primary); padding-top:16px; }
.ml-col-h { font-size:17px; font-weight:700; margin:0; }
.ml-col-b { font-size:14.5px; line-height:1.6; color:var(--color-text-soft); margin:8px 0 0; }

.ml-footer { max-width:1080px; margin:40px auto 0; padding:24px; border-top:1px solid var(--color-border); display:flex; justify-content:space-between; gap:16px; flex-wrap:wrap; font-size:13px; color:var(--color-text-mute); }
.ml-foot-links { display:flex; gap:18px; flex-wrap:wrap; }
.ml-foot-links a { color:var(--color-primary); font-weight:600; }

@media (max-width:640px) { .ml-side { width:120px; } .sk-tiles { grid-template-columns:1fr; } }
@media (prefers-reduced-motion: reduce) {
  .ml-rise, [data-reveal] { opacity:1 !important; transform:none !important; animation:none !important; transition:none !important; }
  .ml-aurora, .ml-screen, .sk-fill { animation:none !important; }
  .sk-fill { width:100%; }
}
`
